import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getApiV1Url } from './config/api';
import DetailInfo from './components/LabList/DetailInfo';
import Filter from './components/Filters/Filter';
import { LabExam } from './LabExam';

type SortField = keyof LabExam | null;
type SortDirection = 'asc' | 'desc';

interface LabExamDeletedPageProps {
  isFilterOpen?: boolean;
  setIsFilterOpen?: (open: boolean) => void;
}

function LabExamDeletedPage({ isFilterOpen: propIsFilterOpen, setIsFilterOpen: propSetIsFilterOpen }: LabExamDeletedPageProps = {}) {
  const navigate = useNavigate();

  const [items, setItems] = useState<LabExam[]>([]);
  const [filteredItems, setFilteredItems] = useState<LabExam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const contentRef = useRef<HTMLDivElement | null>(null);

  const [isFilterOpenLocal, setIsFilterOpenLocal] = useState(false);
  const isFilterOpen = propIsFilterOpen !== undefined ? propIsFilterOpen : isFilterOpenLocal;
  const setIsFilterOpen = propSetIsFilterOpen !== undefined ? propSetIsFilterOpen : setIsFilterOpenLocal;

  const [filteredExamResults, setFilteredExamResults] = useState<string[]>([]);
  const [filteredYears, setFilteredYears] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [filterFilters, setFilterFilters] = useState<{
    manufacturerName: string[];
    manufacturerInn: string[];
    region: string[];
    objectAddress: string[];
  }>({
    manufacturerName: [],
    manufacturerInn: [],
    region: [],
    objectAddress: [],
  });

  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection | null>('asc');

  useEffect(() => {
    const fetchDeleted = async () => {
      setLoading(true);
      setError(null);

      try {
        const access = localStorage.getItem('access');
        const deletedUrl = `${getApiV1Url('LAB_EXAMS_LIST')}deleted/`;

        const response = await axios.get<LabExam[]>(deletedUrl, {
          headers: access ? { Authorization: `Bearer ${access}` } : undefined,
          timeout: 30000,
        });

        setItems(Array.isArray(response.data) ? response.data : []);
      } catch (e) {
        setError('Ошибка загрузки удаленных испытаний.');
      } finally {
        setLoading(false);
      }
    };

    fetchDeleted();
  }, []);

  const applyAllFilters = useMemo(() => {
    return (data: LabExam[]) => {
      let filtered = [...data];

      if (filterFilters.manufacturerName.length > 0) {
        filtered = filtered.filter(item =>
          item.name_company && filterFilters.manufacturerName.some(name =>
            item.name_company.toLowerCase().includes(name.toLowerCase())
          )
        );
      }

      if (filterFilters.manufacturerInn.length > 0) {
        filtered = filtered.filter(item =>
          item.inn_company && filterFilters.manufacturerInn.some(inn =>
            item.inn_company.toLowerCase().includes(inn.toLowerCase())
          )
        );
      }

      if (filterFilters.region.length > 0) {
        filtered = filtered.filter(item =>
          item.region_company && filterFilters.region.some(region =>
            item.region_company.toLowerCase().includes(region.toLowerCase())
          )
        );
      }

      if (filterFilters.objectAddress.length > 0) {
        filtered = filtered.filter(item =>
          item.object_adres && filterFilters.objectAddress.some(address =>
            item.object_adres.toLowerCase().includes(address.toLowerCase())
          )
        );
      }

      if (filteredExamResults.length > 0) {
        filtered = filtered.filter(item =>
          item.exam_result && filteredExamResults.includes(item.exam_result)
        );
      }

      if (filteredYears.length > 0) {
        filtered = filtered.filter(item =>
          item.exam_date && filteredYears.includes(item.exam_date.slice(0, 4))
        );
      }

      if (selectedRegions.length > 0) {
        filtered = filtered.filter(item =>
          item.region_company &&
          selectedRegions.some(region =>
            region && item.region_company.toLowerCase() === region.toLowerCase()
          )
        );
      }

      return filtered;
    };
  }, [filterFilters, filteredExamResults, filteredYears, selectedRegions]);

  useEffect(() => {
    setFilteredItems(applyAllFilters(items));
  }, [items, applyAllFilters]);

  const handleFilterChange = (newFilters: {
    manufacturerName: string[];
    manufacturerInn: string[];
    region: string[];
    objectAddress: string[];
  }) => {
    setFilterFilters(newFilters);
  };

  const handleExamResultChange = (results: string[]) => {
    setFilteredExamResults(results);
  };

  const handleResetFilters = () => {
    setSelectedRegions([]);
    setFilteredExamResults([]);
    setFilteredYears([]);
    setFilterFilters({
      manufacturerName: [],
      manufacturerInn: [],
      region: [],
      objectAddress: [],
    });
  };

  useEffect(() => {
    if (isFilterOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFilterOpen) {
        setIsFilterOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isFilterOpen]);

  const handleRegionSelect = useCallback(
    (region: string) => {
      if (!region || !items) return;

      const exactMatch = items.find(
        (item: LabExam) =>
          item.region_company && region && item.region_company.toLowerCase() === region.toLowerCase()
      );

      const partialMatch = !exactMatch
        ? items.find(
            (item: LabExam) =>
              item.region_company &&
              region &&
              (item.region_company.toLowerCase().includes(region.toLowerCase()) ||
                region.toLowerCase().includes(item.region_company.toLowerCase()))
          )
        : null;

      const matchedRegion = exactMatch ? exactMatch.region_company : (partialMatch ? partialMatch.region_company : null);

      if (matchedRegion) {
        const newSelectedRegions = selectedRegions.some(
          (r: string) => r && matchedRegion && r.toLowerCase() === matchedRegion.toLowerCase()
        )
          ? selectedRegions.filter(
              (r: string) => r && matchedRegion && r.toLowerCase() !== matchedRegion.toLowerCase()
            )
          : [...selectedRegions, matchedRegion];

        setSelectedRegions(newSelectedRegions);
      }
    },
    [items, selectedRegions]
  );

  const sortData = (data: LabExam[]) => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      if (!sortDirection) return 0;

      if (sortField === 'exam_date') {
        const aTime = a.exam_date ? new Date(String(a.exam_date)).getTime() : 0;
        const bTime = b.exam_date ? new Date(String(b.exam_date)).getTime() : 0;
        return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
      }

      const aRaw = a[sortField];
      const bRaw = b[sortField];

      const aStr =
        typeof aRaw === 'boolean'
          ? aRaw
            ? '1'
            : '0'
          : aRaw === undefined || aRaw === null
            ? ''
            : String(aRaw).toLowerCase();

      const bStr =
        typeof bRaw === 'boolean'
          ? bRaw
            ? '1'
            : '0'
          : bRaw === undefined || bRaw === null
            ? ''
            : String(bRaw).toLowerCase();

      const cmp = aStr.localeCompare(bStr, 'ru', { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  };

  const currentItems = useMemo(
    () => sortData(filteredItems),
    [filteredItems, sortField, sortDirection]
  );

  const handleSort = (field: keyof LabExam) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
        return;
      }
      if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
        return;
      }
    }

    setSortField(field);
    setSortDirection('asc');
  };

  const handleOpenLabDetails = (lab: LabExam) => {
    if (!lab.id) return;
    navigate(`/lab-exam/${lab.id}`, { state: { lab } });
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh',
        }}
      >
        Загрузка удаленных испытаний...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <div
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: 24,
            maxWidth: 640,
            margin: '0 auto',
          }}
        >
          <h2 style={{ marginTop: 0 }}>Ошибка</h2>
          <p style={{ color: '#4b5563', lineHeight: 1.5 }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }} ref={contentRef}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Удаленные лабораторные испытания</h2>
      </div>

      {isFilterOpen && (
        <div className="labexam-filter-modal-overlay">
          <div className="labexam-filter-modal" style={{ overflowY: 'visible' }}>
            <button className="labexam-filter-modal__close" onClick={() => setIsFilterOpen(false)}>
              ×
            </button>
            <Filter
              data={items}
              onFilterChange={handleFilterChange}
              examResults={filteredExamResults}
              onExamResultChange={handleExamResultChange}
              onRegionSelect={handleRegionSelect}
              selectedRegions={selectedRegions}
              onResetFilters={handleResetFilters}
              initialFilters={filterFilters}
              open={isFilterOpen}
            />
          </div>
        </div>
      )}

      <DetailInfo
        currentItems={currentItems}
        sortField={sortField}
        sortDirection={sortField ? sortDirection : null}
        onSort={handleSort}
        onRowClick={handleOpenLabDetails}
      />
    </div>
  );
}

export default LabExamDeletedPage;

